import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { parseHttpResponseList, parseHttpResponseItem } from '@shared/functions/parse-http-response';
import { IElectricMeter, ElectricMeter, ElectricMetersApiStatus } from '@shared/models/electric-meter';
import { ElectricMeterSwitchLogEntry } from '@shared/models/electric-meter-switch-log';
import { BcmApi2Service } from '@modules/bcm/@shared/services';

interface ElectricMeterStatusResponse {
    status: ElectricMetersApiStatus;
}

@Injectable({
    providedIn: 'root'
})
export class ElectricMeterApiService extends BcmApi2Service {

    private RESOURCE_ENDPOINT = 'electric-meters';

    getAll(params: HttpParams = new HttpParams()): Observable<ElectricMeter[]> {
        return super.get<IElectricMeter[]>(this.RESOURCE_ENDPOINT, params)
            .pipe(parseHttpResponseList<IElectricMeter, ElectricMeter>(ElectricMeter));
    }

    count(params: HttpParams = new HttpParams()): Observable<number> {
        return super.get<number>(this.RESOURCE_ENDPOINT + '/count', params);
    }

    getOne(id: number, params: HttpParams = new HttpParams()): Observable<ElectricMeter> {
        return super.get<IElectricMeter>(`${this.RESOURCE_ENDPOINT}/${id}`, params)
            .pipe(parseHttpResponseItem<IElectricMeter, ElectricMeter>(ElectricMeter));
    }

    add(body: object = {}): Observable<ElectricMeter> {
        return super.post(this.RESOURCE_ENDPOINT, body);
    }

    update(id: number, body: object = {}): Observable<ElectricMeter> {
        return super.put(`${this.RESOURCE_ENDPOINT}/${id}`, body);
    }

    updateCabinet(id: number, cabinet: any): Observable<number> {
        return super.put(`${this.RESOURCE_ENDPOINT}/${id}/switch-cabinet`, cabinet);
    }

    remove(id: number): Observable<ElectricMeter> {
        return super.delete(`${this.RESOURCE_ENDPOINT}/${id}`);
    }

    turnOn(id: number): Observable<ElectricMeterStatusResponse> {
        return super.put<ElectricMeterStatusResponse>(`${this.RESOURCE_ENDPOINT}/${id}/turn-on`, {});
    }

    turnOff(id: number): Observable<ElectricMeterStatusResponse> {
        return super.put<ElectricMeterStatusResponse>(`${this.RESOURCE_ENDPOINT}/${id}/turn-off`, {});
    }

    getSwitchLog(meterId: number, assignmentId?: number): Observable<ElectricMeterSwitchLogEntry[]> {
        let params = new HttpParams();
        if (assignmentId != null) {
            params = params.set('assignmentId', String(assignmentId));
        }

        return super.get<Array<Omit<ElectricMeterSwitchLogEntry, 'occurredAt'> & { occurredAt: string }>>(
            `${this.RESOURCE_ENDPOINT}/${meterId}/switch-log`,
            params,
        ).pipe(
            map(entries => (entries ?? []).map(entry => ({
                ...entry,
                occurredAt: new Date(entry.occurredAt),
            }))),
        );
    }

    getCurrentReading(id: number): Observable<{ reading: number }> {
        return super.get<{ reading: number }>(`${this.RESOURCE_ENDPOINT}/${id}/current-reading`);
    }

    intermediateReading(
        meterId: number,
        assignmentId: number,
        body: { endDate: Date; endMeterReading?: number; productId?: number },
    ): Observable<{ newReadingId: number; endMeterReading: number }> {
        return super.post<{ newReadingId: number; endMeterReading: number }>(
            `${this.RESOURCE_ENDPOINT}/${meterId}/boats/${assignmentId}/intermediate-reading`,
            body,
        );
    }

    disconnectBoat(
        meterId: number,
        boatId: number,
        body: { id: number; from: Date; to: Date; meterReadingEnd?: number; productId?: number },
    ): Observable<any> {
        return super.put(`${this.RESOURCE_ENDPOINT}/${meterId}/boats/${boatId}/disconnect`, body);
    }

    updateStartReading(
        meterId: number,
        readingId: number,
        body: { startDate: Date; startMeterReading: number },
    ): Observable<{ startMeterReading: number; startDate: string }> {
        return super.put<{ startMeterReading: number; startDate: string }>(
            `${this.RESOURCE_ENDPOINT}/${meterId}/readings/${readingId}/start`,
            body,
        );
    }
}
